/*
 * Define a form page to display a list of choices.
 * Page can include conditional fields and validations.
 */
import * as yup from 'yup'
import { genericMessage, otherLengthMessage } from '../strings/messages'
import { selectionRequired, selectionRequiredMulti } from '../validators'

export const choicePage = propOverrides => {
  const props = {
    pageId: 'needed_page',
    pageTitle: null,
    pageDescription: null,
    fieldType: 'radio',
    fieldName: 'needed',
    choices: [],
    label: 'needed',
    hint: null,
    className: 'needed',
    labelClassName: '',
    validation: null,
    conditionals: [],
    additionalFields: [],
    action: {
      type: 'next'
    },
    ...propOverrides
  }

  // By default, at least one choice must be selected.
  if (props.validation === null)
    props.validation = yup.object().shape({
      [props.fieldName]:
        props.fieldType === 'checkbox'
          ? selectionRequiredMulti()
          : selectionRequired()
    })

  return {
    id: props.pageId,
    title: props.pageTitle,
    description: props.pageDescription,
    validation: props.validation,
    fields: [
      {
        type: props.fieldType,
        name: props.fieldName,
        className: props.className,
        choices: props.choices,
        label: props.label,
        hint: props.hint,
        labelClassName: props.labelClassName,
        conditionals: props.conditionals
      },
      ...props.additionalFields
    ],
    action: props.action
  }
}

/*
 * Define an often used page where the choices includes 'Other'
 * and the user must enter a value for 'Other'.
 */
export const otherChoicePage = propOverrides => {
  const props = {
    conditionalValue: 'Other',
    fieldName: 'needed',
    fieldNameOther: 'needed',
    labelOther: 'Please enter the other',
    hintOther: null,
    maxLengthOther: 150,
    additionalFields: [],
    additionalValidation: null,
    ...propOverrides
  }

  const validation = yup.object().shape({
    [props.fieldName]:
      props.fieldType && props.fieldType === 'checkbox'
        ? selectionRequiredMulti()
        : selectionRequired(),
    [props.fieldNameOther]: yup
      .string()
      .max(props.maxLengthOther, otherLengthMessage)
      .test('select_other_req', genericMessage, function(value) {
        const selectedValue = this.parent[props.fieldName]
        if (selectedValue && selectedValue.includes(props.conditionalValue))
          return value != null
        return true
      }),
    ...props.additionalValidation
  })

  const conditionals = [
    {
      value: props.conditionalValue,
      fields: [
        {
          type: 'text',
          label: props.labelOther,
          hint: props.hintOther,
          name: props.fieldNameOther,
          autoFocus: true
        }
      ]
    }
  ]

  return choicePage({ validation, conditionals, ...props })
}

/*
 * Define an often used page for Yes/No questions
 * Has the option to define the goto page Ids for each answer.
 *  - gotoPageYes
 *  - gotoPageNo
 */
export const yesNoPage = propOverrides => {
  const yes = 'Yes'
  const no = 'No'
  const props = {
    pageId: 'needed_page',
    fieldName: 'needed',
    choices: [{ label: yes, value: yes }, { label: no, value: no }],
    label: 'needed',
    className: 'needed',
    labelClassName: '',
    gotoPageYes: null,
    gotoPageNo: null,
    ...propOverrides
  }

  let page = choicePage({ ...props })
  if (props.gotoPageYes && props.gotoPageNo) {
    page = {
      ...page,
      action: {
        type: 'goto',
        pageId: values => {
          return values[props.fieldName] == yes
            ? props.gotoPageYes
            : props.gotoPageNo
        }
      }
    }
  }
  return page
}

/*
 * Define an often used page for Yes/No questions, with text input if Yes
 */
export const yesNoPlusInputPage = (propOverrides, otherFieldProps) => {
  const props = {
    fieldName: 'needed',
    conditionalValue: 'Yes',
    fieldNameOther: 'yes_input',
    fieldTypeOther: 'text',
    labelOther: 'Please enter the thing',
    hintOther: null,
    valueOther: '',
    classNameOther: '',
    maxLengthOther: 150,
    otherLengthMessage: otherLengthMessage,
    ...propOverrides
  }

  // If validation logic is not given, set the default
  let validation
  if (props.validation) {
    validation = props.validation
  } else {
    validation = yup.object().shape({
      [props.fieldName]: selectionRequired(),
      [props.fieldNameOther]: yup
        .string()
        .max(props.maxLengthOther, props.otherLengthMessage)
        .test('select_yes_req', genericMessage, function(value) {
          const selectedValue = this.parent[props.fieldName]
          if (selectedValue && selectedValue.includes(props.conditionalValue))
            return value != null
          return true
        })
    })
  }

  const conditionals = [
    {
      value: props.conditionalValue,
      fields: [
        {
          type: props.fieldTypeOther,
          label: props.labelOther,
          labelClassName: props.classNameLabelOther,
          hint: props.hintOther,
          name: props.fieldNameOther,
          value: props.valueOther,
          className: props.classNameOther,
          autoFocus: true,
          ...otherFieldProps
        }
      ]
    }
  ]

  return yesNoPage({ validation, conditionals, ...props })
}
